import {
  TestCaseActions,
  TestCaseAttribute,
  ExecutionStrategy,
} from '../components/test-management/types';
import durationToSeconds from '../utils/durationToSeconds';
import { flatten } from '../utils/flatten';
import { pick } from '../utils/pick';

import { User } from './User';

export class TestReportResult {
  readonly step_uuid: string = '';
  readonly number_of_error_test_cases: number = 0;
  readonly number_of_failed_test_cases: number = 0;
  readonly number_of_skipped_test_cases: number = 0;
  readonly number_of_successful_test_cases: number = 0;
  readonly number_of_test_cases: number = 0;

  constructor(props: Partial<TestReportResult> = {}) {
    Object.assign(this, props);
    Object.freeze(this);
  }
}

export class TestCaseReason {
  readonly uuid: string = '';
  readonly message: string = '';
  readonly stack_trace: string = '';

  constructor(props: Partial<TestCaseReason> = {}) {
    Object.assign(this, props);
    Object.freeze(this);
  }

  get logLines(): string[] {
    const logLines: any[] = [];
    if (this.message) {
      logLines.push(...this.message.split('\n'));
      // add extra empty line between message and stack trace
      logLines.push('');
    }

    if (this.stack_trace) {
      logLines.push(...this.stack_trace.split('\n'));
      // add extra empty line for proper rendering of truncated logs
      logLines.push('');
    }
    return logLines;
  }
}

export class TestCaseReasonError {
  readonly error: any = undefined;
  readonly 'error.key': string = '';
  readonly 'error.message': number = 0;

  constructor(props: Partial<TestCaseReasonError> = {}) {
    Object.assign(this, {
      ...pick(flatten(props), Object.keys(this)),
    });
    Object.freeze(this);
  }
}

export type TestCaseResponse = {
  uuid: string;
  name: string;
  status: string;
  duration: string;
  class_name: string;
  package_name: string;
  suite_name: string;
  fully_qualified_name: string;
  custom_properties: Array<{ key: string; value: string }>;
  attributes: string[];
  execution_strategy: string;
  last_action_at?: string;
  last_action_by_user?: User;
  last_action_taken?: TestCaseActions;
  last_updated_at?: string;
  metrics: {
    p90_duration: number;
    p90_duration_in_millis: number;
    p10_duration: number;
    average_duration: number;
    failure_rate: number;
    flakiness_score?: number;
    average_duration_in_millis: number;
    variance_in_millis: number;
  };
};

export class TestCase {
  readonly index: number = -1;
  readonly uuid: string = '';
  readonly name: string = '';
  readonly status: string = '';
  readonly duration: string = '';
  readonly className: string = '';
  readonly packageName: string = '';
  readonly suiteName: string = '';
  readonly fullyQualifiedName: string = '';
  readonly customProperties: Array<{ key: string; value: string }> = [];
  readonly attributes: TestCaseAttribute[] = [];
  readonly executionStrategy: ExecutionStrategy = ExecutionStrategy.DEFAULT;
  readonly lastActionAt?: string;
  readonly lastActionByUser?: User;
  readonly lastActionTaken?: TestCaseActions;
  readonly lastUpdatedAt?: string;
  readonly metrics: {
    p90Duration: number;
    p10Duration: number;
    averageDuration: number;
    failureRate: number;
    flakinessScore?: number;
    p90DurationInMillis: number;
    averageDurationInMillis: number;
    varianceInMillis: number;
  } = {
    p90Duration: 0,
    p10Duration: 0,
    averageDuration: 0,
    failureRate: 0,
    flakinessScore: 0,
    p90DurationInMillis: 0,
    averageDurationInMillis: 0,
    varianceInMillis: 0,
  };

  constructor(props: Partial<TestCaseResponse> = {}, index: number) {
    Object.assign(this, {
      index,
      uuid: props.uuid,
      name: props.name,
      status: props.status,
      duration: props.duration ? durationToSeconds(props.duration) : null,
      className: props.class_name,
      packageName: props.package_name,
      suiteName: props.suite_name,
      fullyQualifiedName: props.fully_qualified_name,
      customProperties: props.custom_properties,
      attributes: (props.attributes || []) as TestCaseAttribute[],
      executionStrategy: (props.execution_strategy ||
        'default') as ExecutionStrategy,
      lastActionAt: props.last_action_at,
      lastActionByUser: props.last_action_by_user
        ? new User(props.last_action_by_user)
        : undefined,
      lastActionTaken: props.last_action_taken as TestCaseActions,
      lastUpdatedAt: props.last_updated_at,
      metrics: {
        p90Duration: props.metrics?.p90_duration,
        p10Duration: props.metrics?.p10_duration,
        averageDuration: props.metrics?.average_duration,
        failureRate: props.metrics?.failure_rate,
        flakinessScore: props.metrics?.flakiness_score,
        p90DurationInMillis: props.metrics?.p90_duration_in_millis,
        averageDurationInMillis: props.metrics?.average_duration_in_millis,
        varianceInMillis: props.metrics?.variance_in_millis,
      },
    });
    Object.freeze(this);
  }
}

export type TestExecutionResponse = {
  uuid: string;
  name: string;
  status: TestReportStatus;
  duration: string;
  test_report_uuid: string;
  test_summary_uuid: string;
  fully_qualified_name: string;
  class_name: string;
  suite_name: string;
  package_name: string;
  build_number: number;
  created_on: string;
  pipeline_uuid: string;
  step_uuid: string;
  run_uuid?: string;
  target: {
    commit: Commit;
    reference_name: string;
    revision: string;
    type: string;
  };
};

export class TestExecution {
  readonly uuid: string = '';
  readonly name: string = '';
  readonly status: string = '';
  readonly duration: string = '';
  readonly testReportUuid: string = '';
  readonly testSummaryUuid: string = '';
  readonly fullyQualifiedName: string = '';
  readonly className: string = '';
  readonly suiteName: string = '';
  readonly packageName: string = '';
  readonly buildNumber: number;
  readonly createdOn: string = '';
  readonly pipelineUuid: string = '';
  readonly stepUuid: string = '';
  readonly target: {
    commit: Commit;
    referenceName: string;
    revision: string;
    type: string;
  } = {
    commit: {
      hash: '',
      message: '',
    },
    referenceName: '',
    revision: '',
    type: '',
  };

  constructor(props: Partial<TestExecutionResponse> = {}) {
    Object.assign(this, {
      uuid: props.uuid,
      name: props.name,
      testReportUuid: props.test_report_uuid,
      testSummaryUuid: props.test_summary_uuid,
      runUuid: props.run_uuid,
      fullyQualifiedName: props.fully_qualified_name,
      className: props.class_name,
      suiteName: props.suite_name,
      packageName: props.package_name,
      buildNumber: props.build_number,
      createdOn: props.created_on,
      status: props.status,
      pipelineUuid: props.pipeline_uuid,
      stepUuid: props.step_uuid,
      target: {
        commit: {
          hash: props.target?.commit?.hash || '',
          message: props.target?.commit?.message || '',
        },
        referenceName: props.target?.reference_name,
        revision: props.target?.revision,
        type: props.target?.type,
      },
      duration: props.duration ? props.duration : null,
    });

    Object.freeze(this);
  }
}

export type Commit = {
  hash: string;
  message: string;
};
export type TestReportStatus = 'SUCCESSFUL' | 'FAILED' | 'ERROR' | 'SKIPPED';
export type TestCaseReasonMap = {
  [key: string]: TestCaseReason | TestCaseReasonError;
};
